import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { captureError, addBreadcrumb } from '@/lib/sentry';
import { debug } from "@/lib/debug";

// Helper to extract error message from Supabase errors
function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    // Supabase errors have { message, details, hint, code }
    if ('message' in err && typeof (err as { message: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
    // Fallback to JSON stringify for debugging
    try {
      return JSON.stringify(err);
    } catch {
      return 'Unknown error';
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

export function useCreatorFollows(walletAddress: string | null) {
  const [following, setFollowing] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFollowing = useCallback(async () => {
    if (!walletAddress) {
      setFollowing([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('creator_follows')
        .select('followed_address')
        .eq('follower_address', walletAddress);

      if (error) throw error;
      setFollowing(data?.map(f => f.followed_address) || []);
    } catch (err) {
      console.error('[useCreatorFollows] Error fetching following:', err);
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchFollowing();
  }, [fetchFollowing]);

  const isFollowing = useCallback((creatorAddress: string) => {
    return following.includes(creatorAddress);
  }, [following]);

  const followCreator = useCallback(async (creatorAddress: string) => {
    if (!walletAddress) return { success: false, error: 'Wallet not connected' };

    // Prevent self-follow
    if (walletAddress === creatorAddress) {
      return { success: false, error: 'You cannot follow yourself' };
    }

    addBreadcrumb('creator_follow.attempt', 'social', {
      follower: walletAddress.slice(0, 8),
      followed: creatorAddress.slice(0, 8),
    });

    try {
      // Use upsert with ignoreDuplicates to handle 409 conflict gracefully
      // If already following, this will succeed without creating a duplicate
      const { error } = await supabase
        .from('creator_follows')
        .upsert(
          { follower_address: walletAddress, followed_address: creatorAddress },
          { onConflict: 'follower_address,followed_address', ignoreDuplicates: true }
        );

      if (error) throw error;

      // Update local state (add if not already present)
      setFollowing(prev => {
        if (prev.includes(creatorAddress)) return prev;
        return [...prev, creatorAddress];
      });

      debug.trace('[useCreatorFollows] Follow success:', creatorAddress.slice(0, 8));
      return { success: true };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error('[useCreatorFollows] Error following:', errorMessage);
      captureError(err instanceof Error ? err : new Error(errorMessage), {
        feature: 'creator_follows',
        action: 'follow',
        follower: walletAddress.slice(0, 8),
        followed: creatorAddress.slice(0, 8),
      });
      return { success: false, error: errorMessage };
    }
  }, [walletAddress]);

  const unfollowCreator = useCallback(async (creatorAddress: string) => {
    if (!walletAddress) return { success: false, error: 'Wallet not connected' };

    addBreadcrumb('creator_unfollow.attempt', 'social', {
      follower: walletAddress.slice(0, 8),
      followed: creatorAddress.slice(0, 8),
    });

    try {
      // Delete the follow record - if it doesn't exist, that's fine (already unfollowed)
      const { error } = await supabase
        .from('creator_follows')
        .delete()
        .eq('follower_address', walletAddress)
        .eq('followed_address', creatorAddress);

      // Supabase delete doesn't error if no rows match, so this handles "already unfollowed" gracefully
      if (error) throw error;

      // Update local state
      setFollowing(prev => prev.filter(a => a !== creatorAddress));

      debug.trace('[useCreatorFollows] Unfollow success:', creatorAddress.slice(0, 8));
      return { success: true };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error('[useCreatorFollows] Error unfollowing:', errorMessage);
      captureError(err instanceof Error ? err : new Error(errorMessage), {
        feature: 'creator_follows',
        action: 'unfollow',
        follower: walletAddress.slice(0, 8),
        followed: creatorAddress.slice(0, 8),
      });
      return { success: false, error: errorMessage };
    }
  }, [walletAddress]);

  return {
    following,
    isLoading,
    isFollowing,
    followCreator,
    unfollowCreator,
    refetch: fetchFollowing,
  };
}

export function useFollowerCount(creatorAddress: string | null) {
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!creatorAddress) {
      setFollowerCount(0);
      setFollowingCount(0);
      setIsLoading(false);
      return;
    }

    const fetchCounts = async () => {
      try {
        const [followers, following] = await Promise.all([
          supabase
            .from('creator_follows')
            .select('id', { count: 'exact', head: true })
            .eq('followed_address', creatorAddress),
          supabase
            .from('creator_follows')
            .select('id', { count: 'exact', head: true })
            .eq('follower_address', creatorAddress),
        ]);

        setFollowerCount(followers.count || 0);
        setFollowingCount(following.count || 0);
      } catch (err) {
        console.error('[useFollowerCount] Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounts();
  }, [creatorAddress]);

  return { followerCount, followingCount, isLoading };
}
